import { Card } from '../ui/card';
import InfoTooltip from '../InfoTooltip';

const STATUS_TONES = {
  Open: 'bg-[#839E97]/20 text-[#839E97] border-[#839E97]/40',
  Limited: 'bg-[#B08A3A]/25 text-[#B08A3A] border-[#B08A3A]/40',
  Closed: 'bg-[#8A3A3A]/20 text-[#8A3A3A] border-[#8A3A3A]/40',
};

function SnapshotTile({ label, value, tone = 'neutral', testId }) {
  const tones = {
    ok:      'bg-[#839E97]/15 border-[#839E97]/40 text-[#839E97]',
    stop:    'bg-[#8A3A3A]/12 border-[#8A3A3A]/40 text-[#8A3A3A]',
    accent:  'bg-[#BAA9CA]/20 border-[#BAA9CA]/50 text-[#685B72]',
    neutral: 'bg-white border-[#E8E5EC] text-[#2D2833]',
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">{label}</div>
      <div className="font-serif text-base mt-1 leading-tight truncate" title={String(value)}>{value}</div>
    </div>
  );
}

function KpiChip({ label, value, testId, tone = 'default' }) {
  const tones = {
    default: 'bg-white border-[#E8E5EC] text-[#2D2833]',
    primary: 'bg-[#685B72] text-white border-transparent',
    accent: 'bg-[#839E97] text-white border-transparent',
    warn: 'bg-[#B08A3A]/20 border-[#E8E5EC] text-[#2D2833]',
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`} data-testid={testId}>
      <div className={`text-[10px] uppercase tracking-widest font-semibold ${tone === 'primary' || tone === 'accent' ? 'text-white/80' : 'text-[#685B72]/80'}`}>{label}</div>
      <div className="font-serif text-2xl mt-1">{value}</div>
    </div>
  );
}

/**
 * Secondary/compact chip for the Pipeline snapshot's second row. Roughly
 * half the visual weight of KpiChip so the primary quartet (Referred /
 * Pre-Auth / Eval Scheduled / Started) reads first, while warn-tone
 * chips (Ready→Therapy > 0, On Hold > 0) still stand out.
 */
function KpiChipSmall({ label, value, testId, tone = 'default' }) {
  const tones = {
    default: 'bg-[#FBF8F4] border-[#E8E5EC] text-[#2D2833]',
    warn: 'bg-[#B08A3A]/15 border-[#B08A3A]/40 text-[#2D2833]',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 flex items-baseline justify-between gap-2 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-[#685B72]/80 truncate">{label}</div>
      <div className="font-serif text-lg tabular-nums leading-none">{value}</div>
    </div>
  );
}

/**
 * Header + scheduling snapshot + pipeline snapshot — the "read-only overview"
 * portion of the ClinicView. All state comes from parent (no mutations here).
 */

// Compose /monthly-report link — defaults to the LAST completed calendar
// month per spec ("defaulting to the most recently completed calendar
// month"). Users can change the month from the report's toolbar.
function monthlyReportHref(clinicName) {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  return `/monthly-report?clinic=${encodeURIComponent(clinicName || '')}&month=${ym}`;
}
export default function ClinicSnapshotPanel({ clinic, kpi }) {
  const statusPill = STATUS_TONES[clinic.clinic_status] || STATUS_TONES.Open;
  return (
    <>
      {/* Header */}
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#685B72]/70 font-semibold">Scheduling Command Center</div>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="font-serif text-4xl text-[#2D2833]">{clinic.name}</h1>
            <a
              href={monthlyReportHref(clinic.name)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-white text-[12px] font-semibold shadow-sm hover:opacity-95"
              style={{ background: '#685B72' }}
              data-testid="clinic-monthly-report-btn"
              title="Generate the printable Monthly Clinic Performance Report for this location"
            >
              Generate Monthly Report
            </a>
          </div>
          <p className="text-[#685B72] mt-2">
            Office Director: <span className="font-semibold text-[#2D2833]">{clinic.office_director}</span> ·
            Priority Evaluator: <span className="font-semibold">{clinic.priority_evaluator || '—'}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`stage-badge border ${statusPill}`} data-testid="clinic-status-pill">
            Clinic status: {clinic.clinic_status || 'Open'}
          </span>
        </div>
      </div>

      {/* Scheduling Snapshot */}
      <Card className="rounded-2xl border-[#E8E5EC] p-5" data-testid="scheduling-snapshot">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <h3 className="font-serif text-lg text-[#2D2833]">Scheduling snapshot</h3>
          <span className="text-[10px] uppercase tracking-widest text-[#685B72]/70 font-semibold">Everything you need before booking a slot</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 text-xs">
          <SnapshotTile label="Speech Evals"        value={clinic.speech_evals_open ? 'Open' : 'Closed'} tone={clinic.speech_evals_open ? 'ok' : 'stop'} testId="snap-speech" />
          <SnapshotTile label="Feeding Evals"       value={clinic.feeding_evals_open ? 'Open' : 'Closed'} tone={clinic.feeding_evals_open ? 'ok' : 'stop'} testId="snap-feeding" />
          <SnapshotTile label="Bilingual Therapy"   value={clinic.bilingual_therapy || '—'} tone={(clinic.bilingual_therapy || '').startsWith('Yes') ? 'ok' : 'neutral'} testId="snap-biling-therapy" />
          <SnapshotTile label="Bilingual Evaluator" value={clinic.bilingual_eval || '—'} tone={clinic.bilingual_eval ? 'ok' : 'neutral'} testId="snap-biling-eval" />
          <SnapshotTile label="Priority Evaluator"  value={clinic.priority_evaluator || '—'} tone="accent" testId="snap-priority-eval" />
          <SnapshotTile label="Max Evals / Week"    value={clinic.max_evals_per_week || '—'} tone="neutral" testId="snap-max-evals" />
          <SnapshotTile label="Eval Location Policy" value={clinic.eval_location_policy || 'In Clinic Only'} tone="neutral" testId="snap-eval-policy" />
          <SnapshotTile label="Office Director"     value={clinic.office_director || '—'} tone="accent" testId="snap-od" />
        </div>
        {(clinic.rules && clinic.rules.length > 0) && (
          <div className="mt-4 rounded-xl bg-[#FAF8FB] border border-[#E8E5EC] p-3" data-testid="snap-rules">
            <div className="text-[10px] uppercase tracking-widest text-[#685B72]/80 font-semibold mb-1.5">Scheduling rules</div>
            <ul className="space-y-0.5 text-xs text-[#2D2833]">
              {clinic.rules.map(r => (
                <li key={r} className="flex gap-2"><span className="text-[#685B72]">•</span><span>{r}</span></li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {/* Pipeline Snapshot */}
      <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="clinic-pipeline-snapshot">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-serif text-xl text-[#2D2833] flex items-center">Pipeline snapshot<InfoTooltip testId="tt-pipeline-snapshot" text="Live counts of clients sitting in each pipeline stage at this clinic right now, based on Main Clinic. 'Started' is lifetime therapy starts; conversion % = started ÷ referred." /></h3>
          <div className="text-xs text-[#685B72]">
            Lifetime conversion <span className="font-serif text-base text-[#685B72]">{kpi.conversion_pct}%</span>
            {' '}· {kpi.referred} referrals → {kpi.started} therapy starts
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiChip label="Referred (lifetime)" value={kpi.referred}         testId="kpi-referred" />
          <KpiChip label="Pre-Auth"            value={kpi.pre_auth_pending}  testId="kpi-pre-auth" />
          <KpiChip label="Eval Scheduled"      value={kpi.eval_scheduled}    testId="kpi-eval-sched" />
          <KpiChip label="Started"             value={kpi.started}           tone="accent" testId="kpi-started" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
          <KpiChipSmall label="Paperwork Pending" value={kpi.paperwork_pending} testId="kpi-pw-pending" />
          <KpiChipSmall label="Ready for Eval"    value={kpi.ready_for_eval}    testId="kpi-ready-eval" />
          <KpiChipSmall label="Ready→Therapy"     value={kpi.ready_therapy}     tone={kpi.ready_therapy > 0 ? 'warn' : 'default'} testId="kpi-ready-therapy" />
          <KpiChipSmall label="On Hold"           value={kpi.on_hold}           tone={kpi.on_hold > 0 ? 'warn' : 'default'} testId="kpi-on-hold" />
        </div>
      </Card>
    </>
  );
}
